import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import { LoadErrorCard, LoadingCard } from "@/components/load-state";
import { Page } from "@/components/page";
import { StatusBadge } from "@/components/status-badge";
import {
  type ConnectorOverview,
  type ConnectorTool,
  type ConnectorToolGroup,
  type ToolGroup,
  useConnectorOverview,
  useMe,
} from "@/lib/api";
import { odooStatus } from "@/lib/connector-status";
import { useLanguage } from "@/lib/i18n/language";
import { withGroupText, withToolText } from "@/lib/i18n/tools";
import { logger } from "@/lib/logger";
import { isAdminRole } from "@/lib/roles";

import { ToolDialog } from "./tool-dialog";
import { ToolFiltersBar } from "./tool-filters";
import { ToolTable } from "./tool-table";
import {
  filterToolSections,
  hasToolFilters,
  isGroupOpen,
  listedGroups,
  listedKinds,
  NO_TOOL_FILTERS,
  parseToolFilters,
  type Serving,
  toolFilterParams,
  type ToolFilters,
  toolGroupSections,
  type ToolKind,
} from "./utils";

/**
 * Tools: the tools the viewer gets in Claude through DC Data, from the
 * backend's overview, which lists them by the viewer's role: a developer
 * every tool, an owner and an admin the catalogue with the tools of their
 * role, a member the tools of their teams and exceptions, and everyone
 * `whoami` and the ideas and questions for Velta. One table without sorting:
 * a header row per group (clients, cranes, orders, the roadmap, ...) that
 * folds, every group open at first, then its tools. A search or a filter
 * shows only the tools that match, every group with a match open. Usage
 * lives on Statistieken. A notice at the
 * top says while the Odoo tools are off, or while the switches could not be
 * read. Filters live in the URL, so a filtered view can be shared. Titles and
 * summaries are in the dashboard's language (`@/lib/i18n/tools`).
 *
 * Everyone sees each tool's technical name, whether it reads or writes, and
 * "Tijdelijk uit" when a developer paused it for everyone, has a switch per
 * tool for themselves ("Voor mij"), and opens a tool's dialog with what
 * Claude reads about it. The admin roles also see who each tool is for
 * ("Voor", one badge per tool that names no team), and a developer pauses a
 * tool for everyone in its dialog.
 */
export function ToolsPage() {
  const { data: me } = useMe();
  const { t } = useTranslation();
  const language = useLanguage();
  const role = me?.role;
  const overviewQuery = useConnectorOverview();
  const overview = overviewQuery.data;
  const tools = overview?.tools ?? [];
  const groups = overview ? listedGroups(overview.groups, tools) : [];
  const kinds = listedKinds(tools);
  // The filters live in the URL (?access=write&q=offerte), so a reload or a shared link keeps them.
  const [searchParams, setSearchParams] = useSearchParams();
  const setFilters = (filters: ToolFilters) =>
    setSearchParams(new URLSearchParams(toolFilterParams(filters)), { replace: true });

  return (
    <Page title={t("nav.tools")} description={t("tools.description")}>
      {overviewQuery.isLoading ? (
        <LoadingCard />
      ) : !overview ? (
        <LoadErrorCard
          message={t("tools.loadError")}
          onRetry={() => void overviewQuery.refetch()}
        />
      ) : (
        <div className="space-y-4">
          <OdooNotice overview={overview} />
          {/* The viewer's own switches, or the developers' switches for everyone, could not be read. */}
          {(!overview.personal_switches_known || !overview.tool_switches_known) && (
            <p className="text-muted text-sm">{t("tools.switchesUnknown")}</p>
          )}
          <ToolsView
            overview={overview}
            groups={withGroupText(groups, language)}
            tools={withToolText(tools, language)}
            kinds={kinds}
            filters={parseToolFilters(searchParams, groups, kinds)}
            onFiltersChange={setFilters}
            role={role}
          />
        </div>
      )}
    </Page>
  );
}

interface ToolsViewProps {
  overview: ConnectorOverview;
  /** The groups the viewer's page lists (`listedGroups`), in the dashboard's language. */
  groups: ConnectorToolGroup[];
  /** The tools the overview lists for the viewer, in the dashboard's language. */
  tools: ConnectorTool[];
  /** The kinds among them (`listedKinds`), for the kind filter. */
  kinds: readonly ToolKind[];
  filters: ToolFilters;
  onFiltersChange: (filters: ToolFilters) => void;
  /** The viewer's dashboard role: the admin roles get "Voor", a developer the switch for everyone. */
  role: string | undefined;
}

/** How long the search waits after the last key before it writes the URL. */
const SEARCH_URL_DELAY_MS = 300;

const NO_GROUPS: ReadonlySet<ToolGroup> = new Set();

/** A set of groups with one group added or taken out. */
function toggled(groups: ReadonlySet<ToolGroup>, group: ToolGroup): Set<ToolGroup> {
  const next = new Set(groups);
  if (!next.delete(group)) {
    next.add(group);
  }
  return next;
}

/**
 * The filter row, the table of the tools that match and a tool's dialog. The
 * search box keeps its own text: the table follows it at once and the URL
 * after a pause, as on the activity page. Bound straight to the URL, the box
 * would jump its cursor, because React Router updates the URL in a
 * transition. Which groups are open is the page's own (`isGroupOpen`):
 * every group but those the viewer folded, without a filter or under the
 * filter that holds.
 */
function ToolsView({
  overview,
  groups,
  tools,
  kinds,
  filters,
  onFiltersChange,
  role,
}: ToolsViewProps) {
  const [search, setSearch] = useState(filters.search);
  // The groups folded without a filter: they stay folded when a filter comes and goes.
  const [folded, setFolded] = useState<ReadonlySet<ToolGroup>>(NO_GROUPS);
  // The groups folded under a filter, for that filter only: another filter opens every match again.
  const [foldedUnder, setFoldedUnder] = useState<{ key: string; groups: ReadonlySet<ToolGroup> }>({
    key: "",
    groups: NO_GROUPS,
  });
  const [openTool, setOpenTool] = useState<string | null>(null);
  useEffect(() => {
    if (search === filters.search) {
      return;
    }
    const timer = setTimeout(() => onFiltersChange({ ...filters, search }), SEARCH_URL_DELAY_MS);
    return () => clearTimeout(timer);
  }, [search, filters, onFiltersChange]);
  const shown: ToolFilters = { ...filters, search };
  const filtered = hasToolFilters(shown);
  const filterKey = JSON.stringify(shown);
  const foldedUnderFilter = foldedUnder.key === filterKey ? foldedUnder.groups : NO_GROUPS;
  const change = (next: ToolFilters) => {
    setSearch(next.search);
    // Another filter changed: write it at once, with the search as typed.
    if (next.search === search) {
      onFiltersChange(next);
    }
  };
  const reset = () => {
    setSearch("");
    onFiltersChange(NO_TOOL_FILTERS);
  };
  const toggle = (group: ToolGroup) => {
    if (filtered) {
      setFoldedUnder({ key: filterKey, groups: toggled(foldedUnderFilter, group) });
    } else {
      setFolded(toggled(folded, group));
    }
  };
  const isAdmin = isAdminRole(role);
  const serving: Serving = {
    mode: overview.mode,
    externalWrites: overview.external_writes,
    odooToolsOn: overview.odoo_tools_on,
  };
  const tool = openTool === null ? null : (tools.find((each) => each.name === openTool) ?? null);
  // A refetch no longer lists the open tool for the viewer (their teams or role changed, or
  // their access could not be read): its dialog closes for good, so it never pops open by
  // itself when a later refetch lists the tool again.
  if (openTool !== null && tool === null) {
    setOpenTool(null);
  }
  const open = (name: string) => {
    logger.debug("[tools]", "open", name);
    setOpenTool(name);
  };

  return (
    <>
      <ToolFiltersBar
        filters={shown}
        onChange={change}
        onReset={reset}
        groups={groups}
        kinds={kinds}
      />
      <ToolTable
        groups={filterToolSections(toolGroupSections(groups, tools), shown)}
        serving={serving}
        showAudience={isAdmin}
        personalSwitchesKnown={overview.personal_switches_known}
        isOpen={(group) => isGroupOpen(group, filtered, folded, foldedUnderFilter)}
        onToggle={toggle}
        onOpen={open}
        onReset={reset}
        search={search}
      />
      <ToolDialog
        tool={tool}
        onClose={() => setOpenTool(null)}
        groupTitle={groups.find((group) => group.id === tool?.group)?.title ?? ""}
        role={role}
        toolSwitchesKnown={overview.tool_switches_known}
        serving={serving}
      />
    </>
  );
}

/**
 * A short notice while the Odoo tools are off, and why: they need the
 * connector on and Odoo connected. Nothing while all is well.
 */
function OdooNotice({ overview }: { overview: ConnectorOverview }) {
  const { t } = useTranslation();
  const odoo = odooStatus(overview);
  if (odoo.on) {
    return null;
  }
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <span className="text-foreground text-sm font-semibold">{t("home.odooTools")}</span>
      <StatusBadge on={odoo.on} label={odoo.label} />
      <span className="text-muted text-sm">{odoo.detail}</span>
    </div>
  );
}
